/**
 * S3 Upload Service for EASI 2.0
 * 
 * HIPAA-COMPLIANT audio file uploads using:
 * - Cognito credentials (authenticated users only)
 * - Customer-managed KMS encryption
 * - Encryption context for data isolation
 */

import { S3Client, PutObjectCommand, GetObjectCommand } from '@aws-sdk/client-s3';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import { awsConfig, getEncryptionContext } from '../config/aws-config';

let s3Client: S3Client | null = null;

/**
 * Initialize S3 client with Cognito credentials
 */
export function initS3Client(idToken: string): S3Client {
  const credentials = fromCognitoIdentityPool({
    clientConfig: { region: awsConfig.region },
    identityPoolId: awsConfig.cognito.identityPoolId,
    logins: {
      [`cognito-idp.${awsConfig.region}.amazonaws.com/${awsConfig.cognito.userPoolId}`]: idToken,
    },
  });

  s3Client = new S3Client({
    region: awsConfig.region,
    credentials,
  });

  return s3Client;
}

/**
 * Get the initialized S3 client
 */
export function getS3Client(): S3Client {
  if (!s3Client) {
    throw new Error('S3 client not initialized. Call initS3Client first.');
  }
  return s3Client;
}

/**
 * Destroy S3 client on logout to prevent stale credential reuse
 */
export function destroyS3Client(): void {
  s3Client = null;
}

/**
 * Upload audio file to S3 with encryption
 */
export async function uploadAudio(
  audioBlob: Blob,
  patientId: string,
  filename: string,
  organizationId: string,
  userId: string,
  onProgress?: (progress: number) => void
): Promise<{ s3Key: string; audioId: string }> {
  const client = getS3Client();
  
  // Generate unique audio ID
  const timestamp = new Date().getTime();
  const randomStr = Math.random().toString(36).substring(2, 11);
  const audioId = `${timestamp}-${randomStr}`;
  const s3Key = `uploads/${userId}/${patientId}/${audioId}/${filename}`;
  
  // Get encryption context for customer data isolation
  const encryptionContext = getEncryptionContext(organizationId, userId);
  
  // Convert blob to array buffer
  const arrayBuffer = await audioBlob.arrayBuffer();
  const uint8Array = new Uint8Array(arrayBuffer);
  
  // Upload with KMS encryption
  const command = new PutObjectCommand({
    Bucket: awsConfig.s3.audioBucket,
    Key: s3Key,
    Body: uint8Array,
    ContentType: audioBlob.type || 'audio/webm',
    ServerSideEncryption: 'aws:kms',
    SSEKMSKeyId: awsConfig.kms.keyAlias,
    Metadata: {
      'patient-id': patientId,
      'organization-id': organizationId,
      'user-id': userId,
      'upload-timestamp': new Date().toISOString(),
      'encryption-context': JSON.stringify(encryptionContext),
    },
  });

  try {
    await client.send(command);
    onProgress?.(100);
    
    return {
      s3Key,
      audioId,
    };
  } catch (error) {
    console.error('S3 upload failed:', error);
    throw new Error(`Failed to upload audio: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Upload report to S3
 */
export async function uploadReport(
  reportData: object,
  patientId: string,
  assessmentId: string,
  organizationId: string,
  userId: string
): Promise<{ s3Key: string }> {
  const client = getS3Client();
  
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  const s3Key = `reports/${userId}/${patientId}/${assessmentId}/report_${timestamp}.json`;
  
  const encryptionContext = getEncryptionContext(organizationId, userId);
  
  const command = new PutObjectCommand({
    Bucket: awsConfig.s3.reportsBucket,
    Key: s3Key,
    Body: JSON.stringify(reportData, null, 2),
    ContentType: 'application/json',
    ServerSideEncryption: 'aws:kms',
    SSEKMSKeyId: awsConfig.kms.keyAlias,
    Metadata: {
      'patient-id': patientId,
      'assessment-id': assessmentId,
      'organization-id': organizationId,
      'encryption-context': JSON.stringify(encryptionContext),
    },
  });

  await client.send(command);
  
  return { s3Key };
}

export default {
  initS3Client,
  getS3Client,
  uploadAudio,
  uploadReport,
};
